// ** React Imports
import { useState, useEffect, ChangeEvent } from 'react'

// ** Type Imports
import { GoogleAdsTagsType, GoogleAdsType } from 'src/lib/types/integrations'

// ** Child Imports
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { Checkbox, Switch, Grid, Typography } from '@mui/material'
import ConfirmationDialog from 'src/components/common/feedback-and-dialog/ConfirmationDialog'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import SaveNotification from 'src/components/common/SaveNotification'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import { PlusSquare, Trash2 } from 'react-feather'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig } from 'src/lib/core/api-methods'
import { initialGoogleAdsAnalyticsState } from 'src/lib/utils/integrationsHelpers'
import { alertSeverites } from 'src/lib/utils/constants'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { useLocale } from 'src/lib/hooks/useLocal'
import { getDefaultChannels } from 'src/lib/utils/helpers'
import { parseGoogleAdsTagsPayload } from 'src/lib/utils/parsers'
import { useActivityLogs, EntityName } from 'src/lib/hooks/useActivityLogs'

interface GoogleAdsFormProps {
  tag: GoogleAdsTagsType
  index: number
  googleAdsAnalytics: GoogleAdsType
  setGoogleAdsAnalytics: React.Dispatch<React.SetStateAction<GoogleAdsType>>
}

const GoogleAdsForm: React.FC<GoogleAdsFormProps> = ({ tag, index, googleAdsAnalytics, setGoogleAdsAnalytics }) => {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false)

  const removeGATag = async () => {
    const modifiedTags = [...googleAdsAnalytics?.tags]
    modifiedTags.splice(index, 1)
    setGoogleAdsAnalytics({
      ...googleAdsAnalytics,
      tags: modifiedTags
    })
  }

  return (
    <>
      <IntegrationChannels
        channels={tag.channels ?? []}
        onChange={(channels: any) => {
          const newTag: GoogleAdsTagsType = { ...tag }
          newTag.channels = channels
          const modifiedTags = [...googleAdsAnalytics?.tags]
          modifiedTags[index] = newTag
          setGoogleAdsAnalytics({ ...googleAdsAnalytics, tags: modifiedTags })
        }}
        hideChannels={!googleAdsAnalytics?.enabled}
      >
        <Grid display={'flex'} flexDirection={'column'} sx={{ gap: 2, width: '100%' }}>
          <div className='flex justify-start gap-2'>
            <div className='flex items-center justify-between w-full'>
              <Typography variant='body1' className='min-w-fit' sx={{ fontWeight: 600 }}>
                Conversion Action {index + 1}
              </Typography>
              <button
                className='flex items-center text-sm font-semibold bg-white text-coal-dark w-fit'
                onClick={() => setIsDeleteConfirmOpen(!isDeleteConfirmOpen)}
              >
                <Trash2 size='16' />
              </button>
            </div>
          </div>
          <Grid item xs={12} md={12} display={'flex'} gap={6} justifyContent={'start'} alignItems={'center'}>
            <Grid display={'flex'} alignItems={'center'} gap={4} width={'100%'}>
              <InputTextField
                errorStatus={tag?.conversionId?.match(/^AW-\d{8,13}$/g) ? false : tag?.conversionId ? true : false}
                textFieldId='ConversionID-Text'
                label={'Conversion ID:'}
                inputProps={{ 'aria-label': 'controlled' }}
                inputValue={tag?.conversionId}
                handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const value = event?.target?.value
                  const newTag = { ...tag }
                  newTag.conversionId = value
                  const modifiedTags = [...googleAdsAnalytics?.tags]
                  modifiedTags[index] = newTag
                  setGoogleAdsAnalytics({
                    ...googleAdsAnalytics,
                    tags: modifiedTags
                  })
                }}
                inputPlaceholder={'AW-XXXXXXXX'}
              />
            </Grid>

            <Grid display={'flex'} alignItems={'center'} gap={4} width={'100%'}>
              <InputTextField
                textFieldId='Conversionlabel-Text'
                label={'Conversion label:'}
                inputProps={{ 'aria-label': 'controlled' }}
                inputValue={tag?.conversionLabel}
                handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const value = event?.target?.value
                  const newTag = { ...tag }
                  newTag.conversionLabel = value
                  const modifiedTags = [...googleAdsAnalytics?.tags]
                  modifiedTags[index] = newTag
                  setGoogleAdsAnalytics({
                    ...googleAdsAnalytics,
                    tags: modifiedTags
                  })
                }}
                inputPlaceholder={'Conversion label'}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Checkbox
                checked={tag?.enhancedConversionEnabled}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const value = event?.target?.checked
                  const newTag = { ...tag }
                  newTag.enhancedConversionEnabled = value
                  const modifiedTags = [...googleAdsAnalytics?.tags]
                  modifiedTags[index] = newTag
                  setGoogleAdsAnalytics({
                    ...googleAdsAnalytics,
                    tags: modifiedTags
                  })
                }}
                inputProps={{ 'aria-label': 'controlled' }}
                sx={{ padding: 0, paddingRight: 1 }}
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography variant='body2'>Enhanced conversions</Typography>
              </div>
            </div>
          </Grid>
        </Grid>
      </IntegrationChannels>
      {isDeleteConfirmOpen ? (
        <ConfirmationDialog
          jobButton={'Yes, remove this action'}
          cancelButton={'No'}
          jobButtonColor='error'
          cancelButtonColor='secondary'
          confirmationText={'Are you sure you want to remove this conversion action?'}
          title={'Remove conversion action'}
          isOpen={isDeleteConfirmOpen}
          setIsConfirmationOpen={setIsDeleteConfirmOpen}
          handleJob={removeGATag}
        />
      ) : (
        <></>
      )}
    </>
  )
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface GoogleAdsProps {
  isOnboarding?: boolean
  handleOnboardingSave?: () => void
}

const GoogleAds: React.FC<GoogleAdsProps> = ({ isOnboarding = false, handleOnboardingSave }) => {
  // Context hook
  const { sendActivityLog } = useActivityLogs()
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { googleAds } = integrations || {}
  const { t } = useLocale()

  //Local State
  const [googleAdsAnalytics, setGoogleAdsAnalytics] = useState<GoogleAdsType>(initialGoogleAdsAnalyticsState)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary) return
    setGoogleAdsAnalytics(temporary.integrations?.googleAds)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(googleAds) !== JSON.stringify(googleAdsAnalytics)))
  }, [googleAdsAnalytics, googleAds])

  useEffect(() => {
    if (!googleAds) return
    setGoogleAdsAnalytics(googleAds)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [googleAds, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            googleAds: googleAdsAnalytics
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response: any = await getIntegrations()
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations(response?.data)
      }
    } catch (error) {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const isValidForm = () => {
    if (googleAdsAnalytics?.enabled) {
      let message = ''
      if (!googleAdsAnalytics?.tags.length) {
        message = 'Please add conversion id and label to enable conversions'
      } else {
        googleAdsAnalytics?.tags?.forEach((tag: GoogleAdsTagsType) => {
          if (!tag?.conversionId.match(/^AW-\d{8,13}$/g)) {
            message = 'Invalid conversion id'
          }
          if (!Boolean(tag.conversionId?.length) || !Boolean(tag.conversionLabel?.length)) {
            message = 'Please fill required google ads conversion fields!'
          }
        })
      }
      if (message) {
        setOpenAlert('error')
        setAlertMessage(message)
        return false
      }
    }
    return true
  }

  const callSubmit = async () => {
    if (!isValidForm()) return
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const payload = {
        enabled: googleAdsAnalytics?.enabled,
        tags: parseGoogleAdsTagsPayload(googleAdsAnalytics?.tags)
      }
      const response: any = await setAnalyticsConfig('google_ad', payload)
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        const activityLogPayload = {
          entityName: EntityName.SETTINGS,
          actionName: 'edited Shop Settings'
        }
        sendActivityLog(activityLogPayload)
        setIntegrations({ ...integrations, googleAds: googleAdsAnalytics }, false)
        setOpenAlert('success')
        setAlertMessage('Successfully saved analytics configuration!')
        handleOnboardingSave && handleOnboardingSave()
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errorMessage
        ? error?.response?.data?.errorMessage
        : `Could not save configuration!`
      handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
      console.error(error)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleDiscardSaving = () => {
    if (!googleAds) return
    setGoogleAdsAnalytics(googleAds)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const onAddGoogleAdsTagsBtnClick = async () => {
    const defaultChannels = await getDefaultChannels()
    const modifiedTags = [...googleAdsAnalytics?.tags]
    modifiedTags.push({
      conversionId: '',
      conversionLabel: '',
      enhancedConversionEnabled: false,
      channels: defaultChannels
    })
    setGoogleAdsAnalytics({
      ...googleAdsAnalytics,
      tags: modifiedTags
    })
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <LayoutContainer>
        <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
          <Grid container>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>Google ads converions is</Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${googleAdsAnalytics?.enabled ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <Switch
                name='googleAdsToggle'
                checked={googleAdsAnalytics?.enabled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const checked = event?.target?.checked
                  setGoogleAdsAnalytics({ ...googleAdsAnalytics, enabled: checked })
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </LayoutContainer>
      {googleAdsAnalytics.tags?.map((tag: GoogleAdsTagsType, index: number) => (
        <GoogleAdsForm
          key={index}
          tag={tag}
          index={index}
          setGoogleAdsAnalytics={setGoogleAdsAnalytics}
          googleAdsAnalytics={googleAdsAnalytics}
        />
      ))}
      <div className='flex flex-wrap gap-4'>
        <button
          className='flex items-center gap-1 px-3 py-2 text-sm font-semibold bg-white border rounded-lg border-flo-gray-light text-coal-dark w-fit'
          onClick={onAddGoogleAdsTagsBtnClick}
        >
          <PlusSquare size='16' />
          Add another conversion action
        </button>
      </div>

      {!Boolean(isOnboarding) ? (
        <SaveNotification onSave={callSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />
      ) : (
        <button
          className='px-3 py-2 ml-auto text-sm font-bold border rounded-lg w-fit text-[#FFFFFF] bg-[#01CCA7]'
          onClick={callSubmit}
        >
          {t('save')}
        </button>
      )}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}
export default GoogleAds
